import { useCallback, useEffect, useRef } from 'react';
import moment from 'moment';

interface UseGenreDateAutoTimeParams {
  /** 品类 → 最新数据日期映射（含 GAME_CATEGORY.ALL 作为全局日期，日期格式 YYYY-MM-DD） */
  byGameGenre?: Record<string, string>;
  /** 时间范围天数（end_time 往前推 N 天为 start_time） */
  dayRange: number;
  /** 筛选器 ref，用于读取最新 filter 值（避免闭包捕获旧值） */
  filterRef: React.MutableRefObject<any>;
  /** 当前 filter 值，用于 useEffect 监听手动修改 */
  filter: any;
}

/**
 * 品类切换自动更新时间筛选器的共享 Hook
 *
 * 核心功能：
 * - 品类切换时，自动将 end_time 设为该品类最新数据日期，start_time 往前推 N 天
 * - byGameGenre 已包含 GAME_CATEGORY.ALL 作为 All Games 的全局日期，统一查表即可
 * - 用户手动修改时间后，停止自动更新
 * - 防重复：同一品类再次切换不重复 dispatch
 *
 * 使用方式：
 * ```tsx
 * const { onGenreChange } = useGenreDateAutoTime({
 *   byGameGenre,
 *   dayRange: 6,
 *   filterRef,
 *   filter,
 * });
 *
 * // 注入到 filterConfigProps 的对应 GameSelect key
 * const filterConfigProps = useMemo(() => ({
 *   ADGame: { onGenreChange },
 * }), [onGenreChange]);
 * ```
 */
export function useGenreDateAutoTime({
  byGameGenre,
  dayRange,
  filterRef,
  filter,
}: UseGenreDateAutoTimeParams) {
  /** 用户是否手动改过时间（一旦手改，品类切换不再自动更新时间） */
  const isTimeManuallyChangedRef = useRef(false);

  /** 最后一次自动设置的时间值，用于：
   *  1. 防重复：onGenreChange 内比对跳过重复 dispatch
   *  2. 手动修改追踪：useEffect 比对 filter 与 lastAutoTimeRef 不一致则标记手改 */
  const lastAutoTimeRef = useRef<{ start_time: string; end_time: string } | null>(null);

  // 监听手动时间修改
  useEffect(() => {
    // filter 初始为 null，无 lastAutoTimeRef 时跳过
    if (!filter || !lastAutoTimeRef.current) return;

    const { start_time: lastStart, end_time: lastEnd } = lastAutoTimeRef.current;
    if (filter.start_time !== lastStart || filter.end_time !== lastEnd) {
      isTimeManuallyChangedRef.current = true;
    }
  }, [filter?.start_time, filter?.end_time]);

  /**
   * 品类切换回调（注入 GameSelect 的 onGenreChange prop）。
   *
   * @param category  - 选中的品类分类值
   * @param _gameIds  - 选中的游戏 ID 列表
   * @returns 额外参数（{ start_time, end_time }），返回 void/undefined 则不追加
   *
   * 仅在以下条件同时满足时返回新时间值：
   * 1. 用户未曾手动修改时间
   * 2. 目标品类有对应日期数据
   * 3. 与上次自动设置的时间值不同（防重复 dispatch）
   */
  const onGenreChange = useCallback(
    (
      category: string,
      _gameIds: string[],
    ): void | { start_time: string; end_time: string } => {
      // 用户已手动修改时间，不再自动更新
      if (isTimeManuallyChangedRef.current) return;

      // 统一从 byGameGenre 查表（已包含 GAME_CATEGORY.ALL）
      const targetDate = byGameGenre?.[category];

      // 品类无对应日期数据，不更新
      if (!targetDate) return;

      const m = moment(targetDate);
      if (!m.isValid()) return;

      // 计算新的时间范围
      const end_time = m.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss');
      const start_time = m
        .clone()
        .subtract(dayRange, 'day')
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss');

      // 防重复：与上次自动设置的时间值相同则不重复 dispatch
      if (
        lastAutoTimeRef.current?.start_time === start_time &&
        lastAutoTimeRef.current?.end_time === end_time
      ) {
        return;
      }

      lastAutoTimeRef.current = { start_time, end_time };

      return { start_time, end_time };
    },
    // 仅依赖 marketConnect 注入的稳定数据（byGameGenre / dayRange）
    [byGameGenre, dayRange],
  );

  return { onGenreChange };
}
